import '#ui/itinerary/itinerary-print.css';

import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';
import type { FlightSegment } from '@elara/domain/models/schemas/flight-schemas';

import type { MapPoint } from '#ui/GoogleMap.js';
import type { BaseItineraryBlockProps } from '#ui/itinerary/BaseItineraryBlock.js';

import { format, subDays } from 'date-fns';
import { isEqual } from 'lodash';
import React from 'react';

import { formatDateRange } from '@elara/client-core/lib/date-utils';
import { getEventTypeIcon, isTravelDay, resolveItemsForEvent } from '@elara/client-core/lib/itinerary-utils';
import { getPlaceIcon } from '@elara/client-core/lib/place-category';
import { calculateTotalsByCurrency, cn, getEmojiFlag } from '@elara/client-core/lib/utils';
import { ItineraryItemEventModel } from '@elara/client-core/models/ItineraryItemEventModel';

import { primeImagesForPrint, printPage, usePrimeImagesBeforePrint } from '#lib/print.js';
import { useItineraryState, useTripsState, useUserProfileState } from '#states/hooks.js';
import { Avatar, AvatarFallback, AvatarImage, Button, CostTotals, GoogleMap, Loader } from '#ui';
import { EmptyState } from '#ui/EmptyState.js';
import { EmptyItineraryIllustration } from '#ui/illustrations/empty-itinerary.js';
import { DayHeader } from '#ui/itinerary/DayHeader.js';
import {
  CustomBlock,
  DestinationArrivalBlock,
  FlightDepartureBlock,
  HotelCheckinBlock,
  HotelCheckoutBlock,
  LayoverBlock,
  PlaceBlock,
} from '#ui/itinerary/index.js';
import { dedupeMapPoints } from '#ui/itinerary/itinerary-map-points.js';
import { ItineraryHero } from '#ui/itinerary/ItineraryHero.js';
import { ItinerarySummarySection } from '#ui/itinerary/ItinerarySummarySection.js';
import { OverviewDestinationHeader } from '#ui/itinerary/OverviewDestinationHeader.js';
import { ShareItineraryButton } from '#ui/itinerary/ShareItineraryButton.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

type EventBlockComponent = (props: BaseItineraryBlockProps) => React.ReactElement | null;

const FlightDepartureBlockComponent: EventBlockComponent = (props) => <FlightDepartureBlock {...props} />;
const HotelCheckinBlockComponent: EventBlockComponent = (props) => <HotelCheckinBlock {...props} />;
const HotelCheckoutBlockComponent: EventBlockComponent = (props) => <HotelCheckoutBlock {...props} />;
const LayoverBlockComponent: EventBlockComponent = (props) => <LayoverBlock {...props} />;
const DestinationArrivalBlockComponent: EventBlockComponent = (props) => <DestinationArrivalBlock {...props} />;
const CustomBlockComponent: EventBlockComponent = (props) => <CustomBlock {...props} />;
const PlaceBlockComponent: EventBlockComponent = (props) => <PlaceBlock {...props} />;

// Map event types to their corresponding block components
const EVENT_BLOCK_COMPONENTS = {
  flight_departure: FlightDepartureBlockComponent,
  hotel_checkin: HotelCheckinBlockComponent,
  hotel_checkout: HotelCheckoutBlockComponent,
  layover: LayoverBlockComponent,
  destination_arrival: DestinationArrivalBlockComponent,
  activity: CustomBlockComponent,
  custom: CustomBlockComponent,
  place: PlaceBlockComponent,
  place_dining: PlaceBlockComponent,
  place_shopping: PlaceBlockComponent,
  place_nature: PlaceBlockComponent,
  place_culture: PlaceBlockComponent,
  place_entertainment: PlaceBlockComponent,
  place_wellness: PlaceBlockComponent,
  place_nightlife: PlaceBlockComponent,
} satisfies Record<string, EventBlockComponent>;

const EDGE_PADDING_CLASSES = 'px-4 md:px-8';

/**
 * EventCard component - renders a single itinerary event
 */
const EventLineItem = React.memo(
  ({
    event,
    item,
    onEventClick,
  }: {
    event: ItineraryItemEventModel;
    item: ItineraryItem;
    onEventClick: (eventId: string) => void;
  }) => {
    const userProfileState = useUserProfileState();
    const homeCountryCode = userProfileState.profile?.countryCode;

    let title = event.title;
    let time = event.formattedTime;

    if (event.type === 'layover') {
      return <div className="@md:ml-[100px] pl-9 @md:pl-4 py-2 text-xs text-muted-foreground">{event.title}</div>;
    }

    let icon = <MaterialIcon icon={getEventTypeIcon(event.type)} size={IconSizes.sm} />;

    if (event.type === 'flight_departure') {
      const segment = event.metadata?.segment as FlightSegment;
      if (segment.airline.logo_url) {
        icon = (
          <Avatar className="h-5 w-5 rounded-none">
            <AvatarImage src={segment.airline.logo_url} alt={segment.airline.name || ''} loading="lazy" />
            <AvatarFallback>{icon}</AvatarFallback>
          </Avatar>
        );
      }
    }

    if (event.type === 'destination_arrival') {
      const segment = event.metadata?.segment as FlightSegment;
      const countryCode = segment.destination?.country_code;

      if (countryCode === homeCountryCode) {
        return null;
      }

      if (countryCode) {
        icon = <div className="w-5">{getEmojiFlag(countryCode)}</div>;
      }
    }

    if (event.type === 'hotel_checkin' || event.type === 'hotel_checkout') {
      time = '';
    }

    if (item.isPlace && item.place && item.place.type) {
      icon = <MaterialIcon icon={getPlaceIcon(item.place.type)} size={IconSizes.sm} />;
    }

    return (
      <div
        className="flex flex-col @md:flex-row @md:items-center gap-1 @md:gap-4 hover:bg-secondary rounded-sm px-4 py-2.5 -mx-4 cursor-pointer"
        onClick={() => onEventClick(event.id)}
      >
        <div className="text-muted-foreground w-[100px] flex-shrink-0 ml-9 @md:ml-0 text-sm @md:text-base">{time}</div>
        <div className="flex items-center gap-4">
          {icon}
          <div>{title}</div>
        </div>
      </div>
    );
  },
  (prev, next) => {
    // Deep comparison of the entire event object
    return isEqual(prev.event, next.event) && isEqual(prev.item, next.item) && prev.onEventClick === next.onEventClick;
  },
);

/**
 * Itinerary tab content - displays the planned itinerary for the trip
 */
export function ItineraryView(): React.ReactNode {
  const eventRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());

  const scrollToEvent = React.useCallback((eventId: string) => {
    const element = eventRefs.current.get(eventId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const userProfileState = useUserProfileState();
  usePrimeImagesBeforePrint();
  const currentTrip = tripsState.current;
  const eventsByDay = itineraryState.eventsByDay;
  const allEvents = itineraryState.events;
  const allItems = itineraryState.items;

  /**
   * Landings are noise on the full-page itinerary: the departure block's timeline already shows the
   * arrival. An arrival stays only as a welcome to a new country with a catalog destination — the
   * same test `DestinationArrivalBlock` applies before it renders anything.
   */
  const isShownEvent = (event: (typeof allEvents)[number]): boolean => {
    if (event.type === 'flight_arrival') return false;
    if (event.type !== 'destination_arrival') return true;
    const destination = (event.metadata?.segment as FlightSegment | undefined)?.destination;
    const countryCode = destination?.country_code;
    if (!countryCode || countryCode === userProfileState.profile?.countryCode) return false;
    return !!itineraryState.getDestinationForArrival({ countryCode, city: destination?.city_name });
  };
  const shownEventsFor = (dateKey: string) => (eventsByDay[dateKey] ?? []).filter(isShownEvent);

  // Ensure AI-generated day titles and trip summary when itinerary is visible and has events
  React.useEffect(() => {
    const tripId = currentTrip?.id;
    if (!tripId || allEvents.length === 0) return;
    const hasExistingSummaries =
      !!currentTrip?.itinerarySummary &&
      !!currentTrip?.itineraryDaySummaries &&
      Object.keys(currentTrip.itineraryDaySummaries).length > 0;
    void itineraryState.generateSummaries(tripId, { hasExistingSummaries });
  }, [
    currentTrip?.id,
    currentTrip?.itinerarySummary,
    currentTrip?.itineraryDaySummaries,
    allEvents.length,
    itineraryState,
  ]);

  // Generate trip days based on actual itinerary events and official dates
  const tripDays = itineraryState.tripDays;

  const mapPoints = allEvents
    .map((event) => {
      if (event.type === 'layover' || event.type === 'flight_departure' || event.type === 'flight_arrival') return;

      const item = allItems.find((i) => i.id === event.itemId);
      if (!item?.mapPoint) return;

      // Model data is the neutral MapPoint; widen to the web MapPoint to attach the DOM onClick.
      const mapPoint: MapPoint = item.mapPoint;
      mapPoint.onClick = () => {
        let type: string = item.type;
        let referenceId: string | undefined | null = item.referenceId;

        if (type === 'room') {
          type = 'hotel';
          // `hotel` resolves from the RoomOption or, for external bookings, from
          // referenceData.hotelId — so this is no longer null for RoomOption-less rooms.
          referenceId = item.hotel?.id ?? item.referenceId;
        }

        if (referenceId) tripsState.showGuide(type, referenceId);
      };

      return mapPoint;
    })
    .filter((point): point is NonNullable<typeof point> => point !== undefined);
  const uniqueMapPoints = dedupeMapPoints(mapPoints);

  if (!itineraryState.itemsReady) {
    return (
      <div className="h-full w-full flex flex-col justify-center items-center p-6">
        <Loader loading={true} />
      </div>
    );
  }

  if (itineraryState.itemsReady && allItems.length === 0) {
    return (
      <div className="h-full w-full flex flex-col justify-center items-center p-6">
        <EmptyState
          title="Itinerary"
          description="Build out your trip itinerary with activities, flights, and hotels. Add something to your itinerary to get started."
          illustration={<EmptyItineraryIllustration width={198} height={140} />}
        />
      </div>
    );
  }

  // Cancelled bookings are no longer part of what the trip costs — mirrors the builder and bookings
  // view totals (ELARA-972).
  const totalCostsByCurrency = calculateTotalsByCurrency(allItems.filter((i) => !i.isCancelled));
  const hasCostTotals = Object.keys(totalCostsByCurrency).length > 0;
  const heroDates =
    currentTrip?.startDate && currentTrip?.endDate
      ? formatDateRange(currentTrip.startDate, currentTrip.endDate)
      : undefined;

  /** One day of the overview list. */
  const renderOverviewDay = (day: (typeof tripDays)[number]) => (
    <div key={day.dayNumber} data-print="overview-day">
      <div className="flex items-center gap-2 border-b border-b-muted pb-2 mb-2.5 text-sm -mr-8">
        <div className="uppercase tracking-wide font-medium">Day {day.dayNumber}</div>
        <div className="text-muted-foreground">•</div>
        <div className="text-muted-foreground">{day.formattedDate}</div>
      </div>

      <div className="flex flex-col">
        {(() => {
          const dateKey = format(day.date, 'yyyy-MM-dd');
          const events = shownEventsFor(dateKey);

          if (isTravelDay(eventsByDay, dateKey, format(subDays(day.date, 1), 'yyyy-MM-dd'))) {
            return <div className="text-sm text-muted-foreground">Traveling</div>;
          }

          if (events.length === 0)
            return <div className="text-sm text-muted-foreground opacity-50">No activities scheduled.</div>;

          return events.map((event) => {
            const items = resolveItemsForEvent(event, allItems);
            if (items.length === 0) return undefined;
            const eventModel = ItineraryItemEventModel.from(event);

            return <EventLineItem key={event.id} event={eventModel} item={items[0]!} onEventClick={scrollToEvent} />;
          });
        })()}
      </div>
    </div>
  );

  return (
    // `data-print-root` scopes the paper layout in `ui/itinerary/itinerary-print.css` (shared with the
    // public shared itinerary); printing from this page prints just the itinerary, not the app around it.
    <div data-print-root="itinerary" className="@container w-full flex flex-col">
      <ItineraryHero
        title={currentTrip?.displayName ?? ''}
        coverImage={currentTrip?.coverImage}
        subtitle={
          (heroDates || hasCostTotals) && (
            <div className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1">
              {heroDates && <span>{heroDates}</span>}
              {heroDates && hasCostTotals && <span aria-hidden>&middot;</span>}
              {hasCostTotals && (
                <CostTotals
                  totalsByCurrency={totalCostsByCurrency}
                  // The "+" between currencies follows the subtitle colour instead of muted grey.
                  className="flex flex-wrap items-baseline gap-1.5 [&>span]:text-current [&>span]:opacity-60"
                />
              )}
            </div>
          )
        }
        actions={
          <Button variant="accent" onClick={() => (tripsState.activePanel = 'bookings')}>
            Book trip
          </Button>
        }
        cornerAction={
          <div className="flex gap-3">
            <Button
              variant={currentTrip?.coverImage ? 'icon-glass' : 'icon'}
              size="icon"
              tooltip="Print itinerary"
              tooltipPosition="left"
              aria-label="Print itinerary"
              // Start fetching the lazy photos as the pointer arrives, so printing rarely has to wait.
              onPointerEnter={primeImagesForPrint}
              onClick={() => {
                if (currentTrip) tripsState.recordItineraryPrinted(currentTrip.id);
                void printPage();
              }}
            >
              <MaterialIcon icon="print" size="sm" />
            </Button>
            <ShareItineraryButton overImage={Boolean(currentTrip?.coverImage)} tooltipPosition="left" />
          </div>
        }
      />

      <ItinerarySummarySection
        summary={currentTrip?.itinerarySummary}
        isLoading={itineraryState.isGeneratingSummaries}
      />

      {/* Trip Overview */}
      <div data-print="overview" className="flex flex-col md:flex-row min-h-[60dvh] border-b">
        <div data-print="overview-list" className={cn('flex-1 flex justify-end')}>
          <div
            className={cn('@container flex flex-col gap-10 w-full max-w-[700px] py-8 md:py-16', EDGE_PADDING_CLASSES)}
          >
            {/* Split by destination, as the itinerary builder does, so a multi-destination trip reads in stops.
                The header travels with its first day (`overview-lead`) so a printed page never ends on it. */}
            {itineraryState.destinationGroups.map((group) => {
              const [firstDay, ...otherDays] = group.days;
              return (
                <div
                  key={`${group.startDateKey}-${group.label}`}
                  data-print="overview-section"
                  className="flex flex-col gap-10"
                >
                  <div data-print="overview-lead" className="flex flex-col gap-10">
                    <OverviewDestinationHeader
                      label={group.label}
                      startDateKey={group.startDateKey}
                      endDateKey={group.endDateKey}
                      dayCount={group.days.length}
                    />
                    {firstDay && renderOverviewDay(firstDay)}
                  </div>
                  {otherDays.map(renderOverviewDay)}
                </div>
              );
            })}
          </div>
        </div>

        <div
          data-print="overview-map"
          className="sticky top-0 flex-1 flex min-h-[60dvh] max-h-[100dvh] bg-background-quaternary"
        >
          {uniqueMapPoints.length > 0 && (
            <GoogleMap points={uniqueMapPoints} zoom={16} className="w-full h-full min-h-[60dvh]" />
          )}
        </div>
      </div>

      {/* Day-by-Day Itinerary */}
      {tripDays.map((day) => {
        const dateKey = format(day.date, 'yyyy-MM-dd');
        const events = shownEventsFor(dateKey);
        const dayTitle = currentTrip?.itineraryDaySummaries?.[dateKey]?.title;
        const isTitleLoading =
          itineraryState.isGeneratingSummaries && !currentTrip?.itineraryDaySummaries?.[dateKey]?.title;

        return (
          <div key={day.dayNumber} data-print="day" className={cn('flex flex-col items-center')}>
            <DayHeader
              number={day.dayNumber}
              date={day.date}
              title={dayTitle}
              // No title yet means one is still coming for the owner (summaries regenerate on view).
              titleLoading={isTitleLoading || !dayTitle}
            />

            {(() => {
              if (isTravelDay(eventsByDay, dateKey, format(subDays(day.date, 1), 'yyyy-MM-dd'))) {
                return (
                  <div
                    data-print="day-note"
                    className="text-muted-foreground text-center px-6 py-12 w-full bg-background-secondary border-b text-sm"
                  >
                    You leapt across time zones and skipped a day. Watch out for jet lag!
                  </div>
                );
              }

              if (events.length === 0) {
                return (
                  <div
                    data-print="day-note"
                    className="text-muted-foreground text-center px-6 py-40 w-full border-b text-sm"
                  >
                    No activities scheduled.
                  </div>
                );
              }

              return events.map((event, index) => {
                const items = resolveItemsForEvent(event, allItems);
                if (items.length === 0) return undefined;

                const eventModel = ItineraryItemEventModel.from(event);

                const BlockComponent = EVENT_BLOCK_COMPONENTS[event.type as keyof typeof EVENT_BLOCK_COMPONENTS];
                const content = BlockComponent ? (
                  <BlockComponent event={eventModel} items={items} size="lg" />
                ) : (
                  event.title
                );

                return (
                  <div
                    key={eventModel.id}
                    ref={(el) => {
                      if (el) eventRefs.current.set(eventModel.id, el);
                    }}
                    data-print="event"
                    className="w-full"
                  >
                    {content}
                  </div>
                );
              });
            })()}
          </div>
        );
      })}
    </div>
  );
}
